import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QuestionnaireService, QuestionAnswer } from '../../services/questionnaireService';
import { Questionnaire, QuestionnaireQuestion, QuestionnaireOption } from '../../types/questionnaire';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { usePoints } from '../../context/PointsContext';
import BasePageLayout from '../../components/BasePageLayout';
import { ContentLanguageIndicator } from '../../components/common/ContentLanguageIndicator';
import { ContentLanguageMetadataService } from '../../services/contentLanguageMetadataService';
import { showToast } from '../../utils/toast';
import '../../styles/pages/TakeQuestionnairePage.css';
import '../../styles/pages/CompactQuestionnaireHeader.css';
import '../../pages/journey/AIIdeaGeneratorPage.css';

const TakeQuestionnairePage: React.FC = () => {
  const { questionnaireId } = useParams<{ questionnaireId: string }>();
  const { user } = useAuth();
  const { uiLanguage, t } = useLanguage();
  const { refreshPoints } = usePoints();
  const navigate = useNavigate();

  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [questions, setQuestions] = useState<QuestionnaireQuestion[]>([]);
  const [answers, setAnswers] = useState<QuestionAnswer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [animatingQuestion, setAnimatingQuestion] = useState(false);
  const [surveyLanguage, setSurveyLanguage] = useState<'en' | 'zh' | null>(null);

  useEffect(() => {
    if (!questionnaireId || !user) {
      setLoading(false);
      setError(t('questionnaireTaker.missingIdOrUser'));
      return;
    }

    const fetchDetails = async () => {
      console.log(`Fetching details for questionnaireId: ${questionnaireId}, userId: ${user?.id}`);
      try {
        setLoading(true);
        const result = await QuestionnaireService.getQuestionnaireForTaking(questionnaireId!, user!.id);
        console.log('getQuestionnaireForTaking result:', result);

        if (result.error) {
          console.error('Error from getQuestionnaireForTaking:', result.error);
          setError(result.error.message || t('questionnaireTaker.failedToLoad'));
        } else if (result.notAvailable) {
          console.warn('Questionnaire not available.');
          setError(t('questionnaireTaker.notAvailable'));
        } else if (result.alreadyTaken) {
          console.warn('Questionnaire already taken.');
          setError(t('questionnaireTaker.alreadyCompleted'));
        } else {
          console.log('Questionnaire data:', result.questionnaire);
          console.log('Questions data:', result.questions);
          console.log('Questions length:', result.questions?.length || 0);

          if (!result.questionnaire) {
            setError(t('questionnaireTaker.notFound'));
            return;
          }

          setQuestionnaire(result.questionnaire);
          setQuestions(result.questions || []);

          // Check survey language from localStorage
          const contentLang = ContentLanguageMetadataService.getContentLanguage('survey', questionnaireId!);
          setSurveyLanguage(contentLang);
          console.log('Survey language:', contentLang, 'User UI language:', uiLanguage);

          if (!result.questions || result.questions.length === 0) {
            console.warn('No questions found for this questionnaire');
            setError(t('questionnaireTaker.noQuestions'));
            return;
          }

          const initialAnswers = (result.questions || []).map(q => ({
            question_id: q.id,
            selected_option_ids: []
          }));
          setAnswers(initialAnswers);
          setError(null); // Clear previous errors on success
        }
      } catch (err: any) {
        console.error('Unexpected error in fetchDetails:', err);
        setError(t('questionnaireTaker.unexpectedError') + ': ' + (err.message || JSON.stringify(err)));
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [questionnaireId, user]);

  const handleOptionChange = (questionId: string, optionId: string, questionType: 'single_choice' | 'multiple_choice') => {
    // Add a brief animation to show selection
    const optionElement = document.getElementById(`option-${optionId}`);
    if (optionElement) {
      optionElement.parentElement?.classList.add('selecting');
      setTimeout(() => {
        optionElement.parentElement?.classList.remove('selecting');
      }, 200);
    }

    setAnswers(prevAnswers =>
      prevAnswers.map(ans => {
        if (ans.question_id === questionId) {
          let newSelectedIds: string[];
          if (questionType === 'single_choice') {
            newSelectedIds = [optionId];
          } else { // multiple_choice
            const currentIndex = ans.selected_option_ids.indexOf(optionId);
            if (currentIndex === -1) {
              newSelectedIds = [...ans.selected_option_ids, optionId];
            } else {
              newSelectedIds = ans.selected_option_ids.filter(id => id !== optionId);
            }
          }
          return { ...ans, selected_option_ids: newSelectedIds };
        }
        return ans;
      })
    );
  };

  const handleSubmit = async () => {
    if (!questionnaireId || !user) {
      setError(t('questionnaireTaker.cannotSubmit'));
      return;
    }

    // Basic validation: ensure all questions have at least one answer if required (can be enhanced)
    for (const question of questions) {
      const answer = answers.find(a => a.question_id === question.id);
      if (!answer || answer.selected_option_ids.length === 0) {
        setError(t('questionnaireTaker.answerAllQuestions', { question: question.question_text }));
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    try {
      const { success, error: submitError } = await QuestionnaireService.submitQuestionnaire(questionnaireId, answers);
      if (success) {
        // Track response language in localStorage
        // Note: We would need the submission ID from the service to track this properly
        // For now, we'll track that the user responded in their UI language
        console.log('Response submitted in language:', uiLanguage);

        // Refresh points to show updated balance immediately
        console.log('🔄 Refreshing points after survey completion...');
        await refreshPoints();
        console.log('✅ Points refreshed successfully');

        showToast(t('questionnaireTaker.submitSuccess'), 'success');
        navigate('/questionnaires'); // Or to a 'completed' page or dashboard
      } else {
        setError((submitError as any)?.message || t('questionnaireTaker.submitFailed'));
      }
    } catch (err: any) {
      setError(t('questionnaireTaker.submitError') + ': ' + (err.message || JSON.stringify(err)));
    } finally {
      setSubmitting(false);
    }
  };

  const isQuestionAnswered = (questionId: string) => {
    const answer = answers.find(ans => ans.question_id === questionId);
    return answer && answer.selected_option_ids.length > 0;
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setAnimatingQuestion(true);
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setAnimatingQuestion(false);
      }, 150);
    }
  };

  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setAnimatingQuestion(true);
      setTimeout(() => {
        setCurrentQuestionIndex(currentQuestionIndex - 1);
        setAnimatingQuestion(false);
      }, 150);
    }
  };

  const goToQuestion = (index: number) => {
    if (index !== currentQuestionIndex) {
      setAnimatingQuestion(true);
      setTimeout(() => {
        setCurrentQuestionIndex(index);
        setAnimatingQuestion(false);
      }, 150);
    }
  };

  const getAnsweredCount = () => {
    return answers.filter(ans => ans.selected_option_ids.length > 0).length;
  };

  // Helper function to detect if this is a validation survey
  const isValidationSurvey = () => {
    return questionnaire?.title?.startsWith('Would You Use This?') || false;
  };

  // Helper function to get validation button class based on option text
  const getValidationButtonClass = (optionText: string): string => {
    const option = optionText.toLowerCase();

    // Map positive responses to btn-yes
    if (option.includes('very likely') || option.includes('daily') || option.includes('definitely') ||
      option.includes('yes') || option.includes('need this now') || option.includes('manual process')) {
      return 'validation-btn btn-yes';
    }

    // Map negative responses to btn-no  
    if (option.includes('never') || option.includes('not interested') || option.includes('definitely not') ||
      option.includes('rarely') || option.includes('no') || option.includes("doesn't affect me")) {
      return 'validation-btn btn-no';
    }

    // Map neutral/maybe responses to btn-maybe
    if (option.includes('maybe') || option.includes('somewhat') || option.includes('monthly') ||
      option.includes('depends') || option.includes('probably not') || option.includes('existing software') ||
      option.includes('workaround') || option.includes('deal with it')) {
      return 'validation-btn btn-maybe';
    }

    // Default to maybe for validation surveys
    return 'validation-btn btn-maybe';
  };

  if (loading) {
    return (
      <div className="take-questionnaire-page">
        <div className="questionnaire-loading">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
          <p className="mt-2">{t('questionnaireTaker.loadingQuestionnaire')}</p>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="take-questionnaire-page">
        <div className="questionnaire-main-content">
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            <strong>{t('common.error')}:</strong> {error}
          </div>
          <button onClick={() => navigate('/questionnaires')} className="btn btn-secondary">
            <i className="bi bi-arrow-left me-2"></i>{t('questionnaireTaker.backToQuestionnaires')}
          </button>
        </div>
      </div>
    );
  }

  if (!questionnaire || questions.length === 0) {
    return (
      <div className="take-questionnaire-page">
        <div className="questionnaire-main-content">
          <div className="alert alert-warning" role="alert">
            <i className="bi bi-question-circle-fill me-2"></i>
            {!questionnaire ? t('questionnaireTaker.notFound') : t('questionnaireTaker.noQuestionsAvailable')}
          </div>
          {!questionnaire && (
            <div className="alert alert-info">
              <strong>{t('questionnaireTaker.debuggingInfo')}:</strong>
              <br />{t('questionnaireTaker.questionnaireId')}: {questionnaireId}
              <br />{t('questionnaireTaker.userId')}: {user?.id}
              <br />{t('questionnaireTaker.checkConsole')}
            </div>
          )}
          <button onClick={() => navigate('/questionnaires')} className="btn btn-secondary">
            <i className="bi bi-arrow-left me-2"></i>{t('questionnaireTaker.backToQuestionnaires')}
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="take-questionnaire-page questionnaire-hub-container">
        {/* Compact Header */}
        <div className="compact-questionnaire-header">
          <div className="compact-header-content">
            {/* Title Section */}
            <div className="compact-title-section">
              <div>
                <button onClick={() => navigate('/questionnaires')} className="btn btn-outline-secondary btn-sm me-3">
                  <i className="bi bi-arrow-left me-2"></i>{t('questionnaireTaker.back')}
                </button>
                <h1 className="compact-page-title">{questionnaire.title}</h1>
                {surveyLanguage && surveyLanguage !== uiLanguage && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <ContentLanguageIndicator
                      contentLanguage={surveyLanguage}
                      originalContent={questionnaire.title}
                      translationContext="survey"
                    />
                  </div>
                )}
                <div className="compact-breadcrumb">
                  <span>{t('questionnaireTaker.questionnaires')}</span>
                  <i className="bi bi-chevron-right compact-breadcrumb-separator"></i>
                  <span>{t('questionnaireTaker.takeSurvey')}</span>
                </div>
              </div>
              <div className="compact-stats-mini">
                <div className="stat-item-mini">
                  <span className="stat-number-mini">{questionnaire.reward_points}</span>
                  <span className="stat-label-mini">{t('questionnaireTaker.pointsReward')}</span>
                </div>
                <div className="stat-item-mini">
                  <span className="stat-number-mini">{currentQuestionIndex + 1}/{questions.length}</span>
                  <span className="stat-label-mini">{t('questionnaireTaker.progress')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Two-Column Layout */}
        <div className="questionnaire-hub-main-two-column">
          {/* Left Column - Progress & Navigation */}
          <div className="questionnaire-progress-column">
            {/* Progress Section */}
            <div className="progress-section">
              <div className="progress-header">
                <h6 className="progress-title">{t('questionnaireTaker.progress')}</h6>
                <span className="progress-percentage">{Math.round((getAnsweredCount() / questions.length) * 100)}%</span>
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${questions.length > 0 ? (getAnsweredCount() / questions.length) * 100 : 0}%`
                  }}
                />
              </div>
              <div className="progress-stats">
                <span className="answered-count">{getAnsweredCount()}</span>
                <span className="total-count"> / {questions.length} {t('questionnaireTaker.answered')}</span>
              </div>
            </div>

            {/* Question Navigation Grid */}
            <div className="question-nav-section">
              <h6 className="nav-title">{t('questionnaireTaker.questions')}</h6>
              <div className="question-nav-grid">
                {questions.map((_, index) => (
                  <button
                    key={index}
                    className={`question-nav-btn ${index === currentQuestionIndex ? 'active' : ''} ${isQuestionAnswered(questions[index].id) ? 'answered' : ''}`}
                    onClick={() => goToQuestion(index)}
                    title={`${t('questionnaireTaker.question')} ${index + 1}${isQuestionAnswered(questions[index].id) ? ` (${t('questionnaireTaker.answered')})` : ''}`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>
            </div>

            {/* Info Panel */}
            <div className="info-panel">
              {questionnaire.description && (
                <div className="description-compact">
                  <h6>{t('questionnaireTaker.description')}</h6>
                  <p>{questionnaire.description}</p>
                </div>
              )}
              <div className="completion-status">
                {getAnsweredCount() < questions.length ? (
                  <div className="status-incomplete">
                    <i className="bi bi-info-circle me-2"></i>
                    {t('questionnaireTaker.answerAllToSubmit')}
                  </div>
                ) : (
                  <div className="status-complete">
                    <i className="bi bi-check-circle-fill me-2"></i>
                    {t('questionnaireTaker.readyToSubmit')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Question & Answers */}
          <div className="questionnaire-question-column">
            {currentQuestion && (
              <div className={`question-container-full ${animatingQuestion ? 'animating' : ''}`}>
                <div className="question-header-main">
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div className="question-number-large" style={{ margin: 0 }}>Q{currentQuestionIndex + 1}</div>
                    <div className="question-type-indicator">
                      <span className={`badge ${currentQuestion.question_type === 'multiple_choice' ? 'bg-warning' : 'bg-info'}`}>
                        <i className={`bi ${currentQuestion.question_type === 'multiple_choice' ? 'bi-check2-square' : 'bi-circle'} me-1`}></i>
                        {currentQuestion.question_type === 'multiple_choice' ? t('questionnaireTaker.multipleChoice') : t('questionnaireTaker.singleChoice')}
                      </span>
                    </div>
                  </div>
                  <div className="question-content">
                    <h3 className="question-text-large">{currentQuestion.question_text}</h3>
                    <div className="question-meta">
                      <div className="question-progress-indicator">
                        <span className="question-count">{currentQuestionIndex + 1} {t('questionnaireTaker.of')} {questions.length}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="options-grid">
                  {currentQuestion.options && currentQuestion.options.length > 0 ? (
                    currentQuestion.options.map((option, optionIndex) => {
                      const currentAnswer = answers.find(ans => ans.question_id === currentQuestion.id);
                      const isSelected = currentAnswer?.selected_option_ids.includes(option.id) || false;

                      return (
                        <div
                          key={option.id}
                          className={`option-card-large ${isSelected ? 'selected' : ''} ${isValidationSurvey() ? getValidationButtonClass(option.option_text) : ''}`}
                          onClick={() => handleOptionChange(currentQuestion.id, option.id, currentQuestion.question_type)}
                          style={{ animationDelay: `${optionIndex * 50}ms` }}
                        >
                          <div className="option-content">
                            <input
                              className="option-input"
                              type={currentQuestion.question_type === 'multiple_choice' ? 'checkbox' : 'radio'}
                              name={`question-${currentQuestion.id}`}
                              id={`option-${option.id}`}
                              value={option.id}
                              checked={isSelected}
                              onChange={() => handleOptionChange(currentQuestion.id, option.id, currentQuestion.question_type)}
                            />
                            <label className="option-label" htmlFor={`option-${option.id}`} dangerouslySetInnerHTML={{ __html: option.option_text }} />
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="alert alert-warning">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {t('questionnaireTaker.noOptionsAvailable')}
                    </div>
                  )}
                </div>

                {/* Navigation Footer at bottom of question column */}
                <div className="navigation-footer">
                  <div className="nav-container">
                    <button
                      className="btn btn-outline-secondary nav-btn"
                      onClick={prevQuestion}
                      disabled={currentQuestionIndex === 0}
                    >
                      <i className="bi bi-chevron-left me-2"></i>{t('questionnaireTaker.previous')}
                    </button>

                    <div className="nav-center">
                      {currentQuestionIndex === questions.length - 1 ? (
                        <button
                          className="btn btn-success btn-lg submit-btn"
                          onClick={handleSubmit}
                          disabled={submitting || getAnsweredCount() < questions.length}
                        >
                          {submitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              {t('questionnaireTaker.submitting')}
                            </>
                          ) : (
                            <>
                              <i className="bi bi-check2-circle me-2"></i>{t('questionnaireTaker.submitQuestionnaire')}
                            </>
                          )}
                        </button>
                      ) : (
                        <div className="progress-indicator-mini">
                          <span className="current-q">{t('questionnaireTaker.question')} {currentQuestionIndex + 1}</span>
                          <span className="total-q">{t('questionnaireTaker.of')} {questions.length}</span>
                        </div>
                      )}
                    </div>


                    {currentQuestionIndex < questions.length - 1 && (
                      <button
                        className="btn btn-primary nav-btn"
                        onClick={nextQuestion}
                      >
                        {t('questionnaireTaker.next')}<i className="bi bi-chevron-right ms-2"></i>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </BasePageLayout>
  );
};

export default TakeQuestionnairePage;
